import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { repairMojibakeDeep } from "../lib/text";

function AdminPanel() {
  const user = getStoredUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const rawCopy = isArabic
    ? {
        eyebrow: "وحدة الإدارة",
        title: "لوحة إدارة المنصة",
        subtitle: "نظرة عامة سريعة على المستخدمين والكورسات والتسليمات، ثم انتقال مباشر إلى وحدات الإدارة التفصيلية.",
        refresh: "تحديث البيانات",
        exportUsers: "تصدير بيانات المستخدمين كاملة",
        totalUsers: "إجمالي المستخدمين",
        students: "الطلبة",
        trainers: "المدربون",
        admins: "المشرفون",
        totalCourses: "إجمالي الكورسات",
        totalAssessments: "إجمالي التقييمات",
        totalSubmissions: "إجمالي التسليمات",
        governorates: "عدد المحافظات المشاركة",
        latestUsers: "أحدث المستخدمين",
        latestUsersHint: "قائمة سريعة بآخر الحسابات مع أهم بيانات التسجيل.",
        searchLabel: "ابحث عن مستخدم",
        searchPlaceholder: "الاسم أو البريد أو المحافظة",
        roleFilter: "فلتر الدور",
        allRoles: "كل الأدوار",
        usersDirectory: "دليل المستخدمين",
        usersDirectoryHint: "يمكنك من هنا تعديل الدور أو حذف الحسابات غير المرتبطة بسجلات مهمة.",
        role: "الدور",
        name: "الاسم",
        email: "البريد",
        phone: "الهاتف",
        governorate: "المحافظة",
        educationLevel: "المرحلة",
        institution: "الجهة التعليمية أو العمل",
        actions: "الإجراءات",
        changeRole: "حفظ الدور",
        deleteUser: "حذف",
        copyEmail: "نسخ البريد",
        studentRole: "طالب",
        trainerRole: "مدرب",
        adminRole: "أدمن",
        noResults: "لا توجد نتائج مطابقة.",
        loading: "جار تحميل بيانات الإدارة...",
        saving: "جار الحفظ...",
        deleting: "جار الحذف...",
        roleSaved: "تم تحديث الدور بنجاح",
        userDeleted: "تم حذف المستخدم بنجاح",
        copiedEmail: "تم نسخ البريد الإلكتروني",
        error: "تعذر تحميل بيانات الإدارة",
        pleaseLogin: "سجّل الدخول بحساب أدمن أولًا.",
        openTrainers: "فتح إدارة المدربين",
        openStudents: "فتح إدارة الطلبة",
        openCourses: "فتح إدارة الكورسات",
        trainersHint: "راجع كل مدرب وكورساتِه ونتائج طلبته.",
        studentsHint: "راجع بيانات كل طالب وتقدمه داخل الكورسات.",
        coursesHint: "راجع الكورسات والنتائج والتقارير العامة.",
        topGovernorates: "أكثر المحافظات مشاركة",
        confirmDeleteTitle: "حذف مستخدم",
        confirmDeleteBody: "سيتم رفض الحذف إذا كان الحساب مرتبطًا ببيانات مهمة داخل المنصة.",
        cancel: "إلغاء",
        confirm: "تأكيد",
        results: "عدد النتائج",
        emptyTopGovernorates: "لا توجد بيانات محافظات بعد."
      }
    : {
        eyebrow: "Admin Unit",
        title: "Platform Administration",
        subtitle: "See the big picture for users, courses, and submissions, then jump into the detailed management workspaces.",
        refresh: "Refresh data",
        exportUsers: "Export full registration data",
        totalUsers: "Total users",
        students: "Students",
        trainers: "Trainers",
        admins: "Admins",
        totalCourses: "Total courses",
        totalAssessments: "Total assessments",
        totalSubmissions: "Total submissions",
        governorates: "Participating governorates",
        latestUsers: "Latest users",
        latestUsersHint: "A quick list of the most recent accounts and their registration data.",
        searchLabel: "Search users",
        searchPlaceholder: "Name, email, or governorate",
        roleFilter: "Role filter",
        allRoles: "All roles",
        usersDirectory: "Users directory",
        usersDirectoryHint: "Update roles or remove accounts that are not tied to critical records.",
        role: "Role",
        name: "Name",
        email: "Email",
        phone: "Phone",
        governorate: "Governorate",
        educationLevel: "Stage",
        institution: "School, university, or workplace",
        actions: "Actions",
        changeRole: "Save role",
        deleteUser: "Delete",
        copyEmail: "Copy email",
        studentRole: "Student",
        trainerRole: "Trainer",
        adminRole: "Admin",
        noResults: "No matching users were found.",
        loading: "Loading admin data...",
        saving: "Saving...",
        deleting: "Deleting...",
        roleSaved: "Role updated successfully",
        userDeleted: "User deleted successfully",
        copiedEmail: "Email copied successfully",
        error: "Unable to load admin data",
        pleaseLogin: "Please sign in with an admin account first.",
        openTrainers: "Open trainer management",
        openStudents: "Open student management",
        openCourses: "Open course management",
        trainersHint: "Review each trainer, their courses, and learner outcomes.",
        studentsHint: "Review student data and progress across courses.",
        coursesHint: "Review course-level performance and reports.",
        topGovernorates: "Top participating governorates",
        confirmDeleteTitle: "Delete user",
        confirmDeleteBody: "Deletion will be blocked if the account is tied to important platform records.",
        cancel: "Cancel",
        confirm: "Confirm",
        results: "results",
        emptyTopGovernorates: "No governorate data yet."
      };
  const copy = repairMojibakeDeep(rawCopy);

  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [pendingRoleByUser, setPendingRoleByUser] = useState({});
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmittingUserId, setIsSubmittingUserId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const roleLabel = (role) => {
    if (role === "admin") {
      return copy.adminRole;
    }
    if (role === "trainer") {
      return copy.trainerRole;
    }
    return copy.studentRole;
  };

  const formatValue = (value) => value || "-";

  const fetchOverview = async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoadingOverview(true);
      const response = await api.get("/admin/overview", { params: { admin_id: user.id } });
      setOverview(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.error);
    } finally {
      setIsLoadingOverview(false);
    }
  };

  const fetchUsers = async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoadingUsers(true);
      const response = await api.get("/users", { params: { admin_id: user.id } });
      const nextUsers = response.data || [];
      setUsers(nextUsers);
      setPendingRoleByUser(
        nextUsers.reduce((accumulator, currentUser) => {
          accumulator[currentUser.id] = currentUser.role;
          return accumulator;
        }, {})
      );
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((currentUser) => {
      const matchesRole = roleFilter === "all" || currentUser.role === roleFilter;
      const matchesSearch =
        !term ||
        [
          currentUser.full_name,
          currentUser.email,
          currentUser.governorate,
          currentUser.education_level,
          currentUser.institution_name
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      return matchesRole && matchesSearch;
    });
  }, [roleFilter, search, users]);

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setFeedbackType("success");
      setFeedback(copy.copiedEmail);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(copy.error);
    }
  };

  const exportUsersCsv = () => {
    if (!users.length) {
      setFeedbackType("error");
      setFeedback(copy.noResults);
      return;
    }

    const columns = [
      ["id", "ID"],
      ["full_name", copy.name],
      ["email", copy.email],
      ["role", copy.role],
      ["phone_number", copy.phone],
      ["birth_date", isArabic ? "تاريخ الميلاد" : "Birth date"],
      ["age", isArabic ? "العمر" : "Age"],
      ["national_id", isArabic ? "الرقم القومي" : "National ID"],
      ["governorate", copy.governorate],
      ["gender", isArabic ? "النوع" : "Gender"],
      ["education_level", copy.educationLevel],
      ["institution_name", copy.institution],
      ["accepted_terms", isArabic ? "وافق على الشروط" : "Accepted terms"]
    ];
    const escapeValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = [
      columns.map((column) => column[1]),
      ...users.map((currentUser) =>
        columns.map(([key]) => {
          if (key === "role") {
            return roleLabel(currentUser[key]);
          }
          if (key === "accepted_terms") {
            return currentUser[key] ? "Yes" : "No";
          }
          return currentUser[key];
        })
      )
    ];
    const csv = rows.map((row) => row.map(escapeValue).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "learnova-users-full.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setFeedbackType("success");
    setFeedback(copy.exportUsers);
  };

  const saveUserRole = async (targetUser) => {
    const nextRole = pendingRoleByUser[targetUser.id];
    if (!nextRole || nextRole === targetUser.role) {
      return;
    }

    try {
      setIsSubmittingUserId(targetUser.id);
      await api.patch(`/admin/users/${targetUser.id}/role`, {
        admin_id: user?.id,
        role: nextRole
      });
      setFeedbackType("success");
      setFeedback(copy.roleSaved);
      await fetchOverview();
      await fetchUsers();
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.error);
    } finally {
      setIsSubmittingUserId(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!confirmState?.userId) {
      return;
    }

    try {
      setIsSubmittingUserId(confirmState.userId);
      await api.delete(`/admin/users/${confirmState.userId}`, {
        params: { admin_id: user?.id }
      });
      setFeedbackType("success");
      setFeedback(copy.userDeleted);
      setConfirmState(null);
      await fetchOverview();
      await fetchUsers();
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.error);
    } finally {
      setIsSubmittingUserId(null);
    }
  };

  const stats = overview?.stats || {};
  const latestUsers = overview?.latest_users || [];
  const topGovernorates = overview?.top_governorates || [];

  return (
    <section className="workspace-page admin-page">
      <div className="hero-panel admin-overview-hero">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
        <div className="action-cluster">
          <button className="secondary-button" type="button" onClick={() => { fetchOverview(); fetchUsers(); }}>
            {copy.refresh}
          </button>
          <button className="primary-button" type="button" onClick={exportUsersCsv}>
            {copy.exportUsers}
          </button>
        </div>
      </div>

      {feedback && <div className={`feedback-banner feedback-banner--${feedbackType}`}>{feedback}</div>}

      {!user?.id && (
        <div className="panel-card state-panel state-panel--error">
          <p>{copy.pleaseLogin}</p>
        </div>
      )}

      <section className="metric-grid admin-summary-grid">
        <article className="metric-card admin-stat-card">
          <span>{copy.totalUsers}</span>
          <strong>{stats.total_users || 0}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.totalCourses}</span>
          <strong>{stats.total_courses || 0}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.totalSubmissions}</span>
          <strong>{stats.total_submissions || 0}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.trainers}</span>
          <strong>{stats.trainers_count || 0}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.students}</span>
          <strong>{stats.students_count || 0}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.governorates}</span>
          <strong>{stats.governorates_count || 0}</strong>
        </article>
      </section>

      <section className="admin-quick-grid">
        <article className="panel-card admin-quick-card">
          <h3>{copy.openTrainers}</h3>
          <p className="helper-text">{copy.trainersHint}</p>
          <button className="ghost-button" type="button" onClick={() => navigate("/admin/trainers")}>
            {copy.openTrainers}
          </button>
        </article>
        <article className="panel-card admin-quick-card">
          <h3>{copy.openStudents}</h3>
          <p className="helper-text">{copy.studentsHint}</p>
          <button className="ghost-button" type="button" onClick={() => navigate("/admin/students")}>
            {copy.openStudents}
          </button>
        </article>
        <article className="panel-card admin-quick-card">
          <h3>{copy.openCourses}</h3>
          <p className="helper-text">{copy.coursesHint}</p>
          <button className="ghost-button" type="button" onClick={() => navigate("/admin/courses")}>
            {copy.openCourses}
          </button>
        </article>
      </section>

      <section className="two-column-grid">
        <div className="panel-card">
          <div className="card-title-row">
            <div>
              <h3>{copy.topGovernorates}</h3>
              <p className="helper-text">{copy.latestUsersHint}</p>
            </div>
          </div>
          {isLoadingOverview ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : topGovernorates.length ? (
            <div className="stack-md">
              {topGovernorates.map((item) => (
                <div key={item.governorate} className="admin-list-row">
                  <strong>{item.governorate}</strong>
                  <span className="status-pill">{item.users_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="state-panel state-panel--empty"><p>{copy.emptyTopGovernorates}</p></div>
          )}
        </div>

        <div className="panel-card">
          <div className="card-title-row">
            <div>
              <h3>{copy.latestUsers}</h3>
              <p className="helper-text">{copy.latestUsersHint}</p>
            </div>
          </div>
          {isLoadingOverview ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : latestUsers.length ? (
            <div className="stack-md">
              {latestUsers.map((currentUser) => (
                <div key={currentUser.id} className="admin-list-row">
                  <div>
                    <strong>{currentUser.full_name}</strong>
                    <p className="helper-text">{currentUser.email}</p>
                  </div>
                  <span className="status-pill">{roleLabel(currentUser.role)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="state-panel state-panel--empty"><p>{copy.noResults}</p></div>
          )}
        </div>
      </section>

      <div className="panel-card admin-panel-shell">
        <div className="card-title-row">
          <div>
            <h3>{copy.usersDirectory}</h3>
            <p className="helper-text">{copy.usersDirectoryHint}</p>
          </div>
          <div className="admin-users-toolbar">
            <div className="field-stack admin-search-field">
              <label className="field-label">{copy.searchLabel}</label>
              <input
                className="text-input"
                type="search"
                value={search}
                placeholder={copy.searchPlaceholder}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="field-stack">
              <label className="field-label">{copy.roleFilter}</label>
              <select className="text-input" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                <option value="all">{copy.allRoles}</option>
                <option value="student">{copy.studentRole}</option>
                <option value="trainer">{copy.trainerRole}</option>
                <option value="admin">{copy.adminRole}</option>
              </select>
            </div>
            <span className="status-pill">
              {filteredUsers.length} {copy.results}
            </span>
          </div>
        </div>

        {isLoadingUsers ? (
          <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
        ) : !filteredUsers.length ? (
          <div className="state-panel state-panel--empty"><p>{copy.noResults}</p></div>
        ) : (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{copy.name}</th>
                  <th>{copy.email}</th>
                  <th>{copy.phone}</th>
                  <th>{copy.governorate}</th>
                  <th>{copy.educationLevel}</th>
                  <th>{copy.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((currentUser) => (
                  <tr key={currentUser.id}>
                    <td>
                      <div className="admin-user-identity">
                        <strong>{currentUser.full_name}</strong>
                        <span className="muted-text">{roleLabel(currentUser.role)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="admin-user-actions">
                        <span>{currentUser.email}</span>
                        <button className="ghost-button" type="button" onClick={() => copyText(currentUser.email)}>
                          {copy.copyEmail}
                        </button>
                      </div>
                    </td>
                    <td>{formatValue(currentUser.phone_number)}</td>
                    <td>{formatValue(currentUser.governorate)}</td>
                    <td>
                      <div className="stack-sm">
                        <span>{formatValue(currentUser.education_level)}</span>
                        <span className="muted-text">{formatValue(currentUser.institution_name)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="admin-user-actions">
                        <select
                          className="text-input admin-role-select"
                          value={pendingRoleByUser[currentUser.id] || currentUser.role}
                          onChange={(event) =>
                            setPendingRoleByUser((current) => ({
                              ...current,
                              [currentUser.id]: event.target.value
                            }))
                          }
                          disabled={isSubmittingUserId === currentUser.id}
                        >
                          <option value="student">{copy.studentRole}</option>
                          <option value="trainer">{copy.trainerRole}</option>
                          <option value="admin">{copy.adminRole}</option>
                        </select>
                        <button
                          className="ghost-button"
                          type="button"
                          onClick={() => saveUserRole(currentUser)}
                          disabled={
                            isSubmittingUserId === currentUser.id ||
                            (pendingRoleByUser[currentUser.id] || currentUser.role) === currentUser.role
                          }
                        >
                          {isSubmittingUserId === currentUser.id ? copy.saving : copy.changeRole}
                        </button>
                        <button
                          className="danger-button"
                          type="button"
                          onClick={() =>
                            setConfirmState({
                              userId: currentUser.id,
                              name: currentUser.full_name
                            })
                          }
                          disabled={isSubmittingUserId === currentUser.id}
                        >
                          {isSubmittingUserId === currentUser.id ? copy.deleting : copy.deleteUser}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmState && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{copy.confirmDeleteTitle}</h3>
            <p>{confirmState.name}</p>
            <p className="helper-text">{copy.confirmDeleteBody}</p>
            <div className="modal-actions">
              <button className="secondary-button" type="button" onClick={() => setConfirmState(null)}>
                {copy.cancel}
              </button>
              <button className="danger-button" type="button" onClick={confirmDeleteUser}>
                {copy.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AdminPanel;
